

import { api } from './api';

// Maps a raw public.business_apps row (as returned by GET /api/apps) into
// the shape the business dashboard/applications UI renders. Kept in one
// place so every screen that lists apps sees the same fields.
const normalizeBusinessApp = (row) => {
  const isBlocked = Boolean(row.blockDetails?.isBlocked);
  const status = isBlocked ? 'INACTIVE' : row.enabled ? 'ACTIVE' : 'PAUSED';

  return {
    appId: row._id,
    name: row.appName,
    type: row.appType,
    description: row.appDescription,
    status,
    enabled: row.enabled,
    allowedDomains: row.allowedDomains || [],
    domains: row.domainDetails || [],
    metadata: row.metadata || {},
    createdAt: row.createdAt,
    updatedAt: row.updatedAt,
    // Not tracked on business_apps itself — left at 0 until a
    // subscribers/transactions-by-app endpoint is wired in.
    subscriberCount: row.subscriberCount ?? 0,
    transactionCount7d: row.transactionCount7d ?? 0,
  };
};

export const businessService = {
  // Get business dashboard data - works without backend API
  getDashboard: async () => {
    console.log('📊 [BUSINESS SERVICE] Fetching dashboard from API');
    
    try {
      // Try to get business profile from backend
      const response = await api.get('/api/business/profile');
      console.log('✅ API response:', response.data);

      if (response.data.success && response.data.data) {
        // Return the business data directly
        return response.data.data;
      } else {
        throw new Error('Invalid API response');
      }
    } catch (error) {
      console.warn('⚠️ API failed, falling back to localStorage:', error.message);
      
      // Fallback to localStorage (for development/offline)
      const userStr = localStorage.getItem('user');
      const authType = localStorage.getItem('authType');
      let user = {};
      if (userStr) {
        try {
          user = JSON.parse(userStr);
        } catch (e) {
          console.error('Error parsing user:', e);
        }
      }

      return {
        businessName: user.businessName || 'Your Business',
        industry: localStorage.getItem('industry') || user.industry || 'Not specified',
        contactEmail: user.username || 'Not available',
        username: user.username || 'Not available',
        authType: authType || 'IN_APP',
        accountStatus: user.accountStatus || 'ACTIVE',
        createdAt: user.createdAt || new Date().toISOString(),
        totalApplications: 0,
        applications: []
      };
    }
  },

  // Get business profile - try API but fallback to localStorage
  getBusinessDetails: async () => {
    console.log('📊 [BUSINESS SERVICE] Getting business details');
    
    try {
      // Try API first
      const response = await api.get('/api/business/profile');
      console.log('✅ API response for profile:', response.data);
      
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error('Invalid API response');
      
    } catch (apiError) {
      console.log('⚠️ API failed, using localStorage:', apiError.message);
      
      // Fallback to localStorage
      const userStr = localStorage.getItem('user');
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          return {
            businessName: user.businessName,
            industry: user.industry,
            email: user.email,
            username: user.username,
            accountStatus: user.accountStatus,
            createdAt: user.createdAt
          };
        } catch (parseError) {
          console.error('Error parsing user:', parseError);
        }
      }
      
      // Ultimate fallback
      return {
        businessName: 'Your Business',
        industry: 'Not specified',
        email: 'Not available',
        username: 'Not available',
        accountStatus: 'ACTIVE',
        createdAt: new Date().toISOString()
      };
    }
  },

  // List the apps (business_apps rows) that belong to the logged-in
  // business. The real endpoint is GET /api/businesses/apps/:id, scoped
  // by businessId in the path — the backend still rejects any id that
  // isn't the caller's own (403), so we first resolve *our* business _id
  // from /api/business/profile rather than trusting anything cached.
  getApplications: async () => {
    try {
      const profileRes = await api.get('/api/business/profile');
      const businessId = profileRes.data?.data?.id;
      if (!businessId) {
        console.log('Could not resolve business id from profile');
        return [];
      }

      const response = await api.get(`/api/businesses/apps/${businessId}`, {
        params: { limit: 100 },
      });
      const rows = response.data?.data || [];
      return rows.map(normalizeBusinessApp);
    } catch (error) {
      console.log('Applications endpoint not available:', error.message);
      return [];
    }
  },

  getApplication: (appId) => api.get(`/api/business-apps/${appId}`),

  updateApplication: (appId, data) => 
    api.put(`/api/business/applications/${appId}`, data),

  getAppToken: (appId) => 
    api.post(`/api/business/applications/${appId}/token`),
};